import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';

import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { addItemToGoal } from 'Shared/actions/todos';
import { fetchGoals } from 'Shared/actions/Goals.actions';
import { getGoalsByUserIdAndComplete } from 'Shared/selectors/Goals.selectors';
import { getMeetingItemByUuid } from 'Shared/selectors/common';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import WpAlert from 'Wpds/components/alert/WpAlert';

import { formatWorkspaceTitle } from 'Shared/helpers';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  itemUuid: string;
  isOpen: boolean;
  onToggle: () => void;
};

const AddToGoalModal = ({ itemUuid, isOpen, onToggle }: Props) => {
  const { user: currentUser, id: userId } = useCurrentUser();
  const goals = useSelector(state => getGoalsByUserIdAndComplete(state, { complete: false, userId }))
    .valueSeq()
    .toJS();
  const currentWorkspaceSlug = useSelector(getMeetingItemByUuid(itemUuid)).workspaceId;
  const workspaces = useSelector(state => state.workspaces);
  const goalSlug = useSelector(getMeetingItemByUuid(itemUuid)).goalSlug;

  const dispatch = useDispatch();
  const [goal, setGoal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [moveToWorkspaceName, setMoveToWorkspaceName] = useState(null);

  const optionsForSelect = goals ? goals.map(({ slug, name }) => ({ value: slug, label: name })) : [];

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Add To Goal' });
    dispatch(fetchGoals({ context: 'team', completed: false }));
  }, []);

  // Set the alert to inform user about moving the item to a different workspace
  useEffect(() => {
    const selectedGoal = goals.find(g => goal && g.slug === goal.value);
    const moveToWorkspace = workspaces.find(w => selectedGoal && w.id === selectedGoal.workspaceSlug);
    const isDifferentWorkspace = moveToWorkspace ? moveToWorkspace.id !== currentWorkspaceSlug : false;
    if (isDifferentWorkspace) {
      setMoveToWorkspaceName(formatWorkspaceTitle({ workspace: moveToWorkspace, user: currentUser }));
    } else {
      setMoveToWorkspaceName(null);
    }
  }, [currentUser, currentWorkspaceSlug, goal, goals, workspaces]);

  // Set the option to the current goal if it's already set
  useEffect(() => {
    const option = optionsForSelect.find(({ value }) => value === goalSlug);
    setGoal(option);
  }, [goalSlug, JSON.stringify(optionsForSelect)]);

  const handleSelectChange = value => {
    setGoal(value);
    return null;
  };

  const handleCreateClick = () => {
    setSaving(true);
    dispatch(addItemToGoal({ itemUuid, goalId: goal.value })).finally(() => {
      setSaving(false);
      onToggle();
    });
  };

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>Add To Goal</WpModalHeader>
      <WpModalBody>
        <div className="mb-2">Choose the goal you would like to add this item to.</div>
        <Select
          isSearchable
          menuPortalTarget={document.body}
          options={optionsForSelect}
          placeholder="Choose goal"
          value={goal}
          onChange={handleSelectChange}
          styles={{ menuPortal: base => ({ ...base, zIndex: '1050' }) }}
        />
        {moveToWorkspaceName && (
          <div className="mt-3">
            <WpAlert>We&apos;ll move this item into the same workspace ({moveToWorkspaceName}) as the goal</WpAlert>
          </div>
        )}
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" disabled={saving} onClick={onToggle}>
            Cancel
          </WpButton>
          <WpButton disabled={saving || !goal} loading={saving} onClick={handleCreateClick}>
            Save
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default AddToGoalModal;
